/**
 * Bridge between @clerk/clerk-react and the openapi-fetch client.
 *
 * `<ClerkAuthBridge />` is mounted once inside `<ClerkProvider>`. It pushes
 * Clerk's `getToken` and `signOut` into the api client so requests pick up a
 * fresh JWT and 401 responses can trigger a clean sign-out + redirect — all
 * without React components needing to know about the wiring.
 *
 * It also watches tab-focus: if the user comes back after a long sleep / dead
 * network and Clerk is stuck retrying a broken session refresh (422 on
 * /v1/client/sessions/.../tokens), we end the session ourselves to break the
 * retry loop, then redirect to /login.
 */

import { useAuth, useClerk } from "@clerk/clerk-react";
import { useEffect } from "react";
import { setAuthTokenGetter, setAuthSignOut } from "./api/client";

export function ClerkAuthBridge() {
  const { getToken, isSignedIn } = useAuth();
  const { signOut } = useClerk();

  useEffect(() => {
    setAuthTokenGetter(async () => getToken());
  }, [getToken]);

  useEffect(() => {
    setAuthSignOut(async () => {
      await signOut();
    });
  }, [signOut]);

  useEffect(() => {
    async function checkAuthOnFocus() {
      if (document.visibilityState !== "visible") return;
      if (!isSignedIn) return;
      try {
        const t = await getToken();
        if (!t) throw new Error("null token");
      } catch {
        // Clerk thinks we're signed in but can't issue a token. Session is
        // dead (expired server-side, or wedged after sleep). End it cleanly
        // — this stops Clerk's background `refreshSessionToken` retries.
        try {
          await signOut();
        } catch {
          /* ignore — we're redirecting anyway */
        }
        window.location.assign("/login");
      }
    }
    document.addEventListener("visibilitychange", checkAuthOnFocus);
    return () => {
      document.removeEventListener("visibilitychange", checkAuthOnFocus);
    };
  }, [getToken, isSignedIn, signOut]);

  return null;
}
